/**
 * Utilidad para validación y formateo de RUT chileno
 */

/**
 * Valida un RUT chileno según el algoritmo del dígito verificador
 * @param {string} rut - RUT a validar (puede incluir puntos y guión)
 * @returns {boolean} - true si el RUT es válido, false en caso contrario
 */
export const validarRUT = (rut) => {
  // Limpiar el RUT de puntos y guiones
  const rutLimpio = rut.replace(/\./g, "").replace(/-/g, "");
  
  if (rutLimpio.length < 8 || rutLimpio.length > 10) {
    return false;
  }
  
  // Separar el cuerpo del dígito verificador
  const cuerpo = rutLimpio.slice(0, -1);
  const dv = rutLimpio.slice(-1).toUpperCase();
  
  // Calcular el dígito verificador
  let suma = 0;
  let multiplicador = 2;
  
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo.charAt(i)) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }
  
  const dvEsperado = 11 - (suma % 11);
  const dvCalculado = dvEsperado === 11 ? "0" : dvEsperado === 10 ? "K" : dvEsperado.toString();
  
  return dv === dvCalculado;
};

/**
 * Estima el rango de año de nacimiento aproximado según el número de RUT chileno.
 * Solo aplica a RUTs de chilenos (< 20 millones). Retorna null para extranjeros
 * o cuando no se puede estimar.
 * @param {string} rut - RUT formateado o sin formato
 * @returns {{ min: number, max: number } | null}
 */
export const estimarRangoNacimientoDesdeRUT = (rut) => {
  const rutLimpio = rut.replace(/\./g, "").replace(/-/g, "");
  if (rutLimpio.length < 2) return null;

  const cuerpo = parseInt(rutLimpio.slice(0, -1));
  if (isNaN(cuerpo)) return null;

  // RUTs >= 20M suelen ser extranjeros — sin estimación confiable
  if (cuerpo >= 20_000_000) return null;

  if (cuerpo < 5_000_000)  return { min: 1900, max: 1950 };
  if (cuerpo < 8_000_000)  return { min: 1950, max: 1967 };
  if (cuerpo < 11_000_000) return { min: 1963, max: 1980 };
  if (cuerpo < 14_000_000) return { min: 1976, max: 1988 };
  if (cuerpo < 16_000_000) return { min: 1983, max: 1993 };
  if (cuerpo < 18_000_000) return { min: 1988, max: 1997 };
  return { min: 1993, max: 2003 }; // 18M–20M
};

/**
 * Verifica si el año de nacimiento ingresado es coherente con el RUT chileno.
 * Usa un margen de 12 años para tolerar variaciones (inscripciones tardías, etc.).
 * @param {string} rut - RUT con o sin formato
 * @param {number|string} anioNacimiento - Año de nacimiento (ej: 1990)
 * @returns {{ valido: boolean, advertencia: string | null }}
 */
export const validarCoherenciaRUTFecha = (rut, anioNacimiento) => {
  const rango = estimarRangoNacimientoDesdeRUT(rut);
  if (!rango) return { valido: true, advertencia: null };

  const anio = parseInt(anioNacimiento);
  if (isNaN(anio)) return { valido: true, advertencia: null };

  const MARGEN = 12;
  if (anio < rango.min - MARGEN || anio > rango.max + MARGEN) {
    return {
      valido: false,
      advertencia: "La fecha de nacimiento no parece corresponder al RUT ingresado",
    };
  }
  return { valido: true, advertencia: null };
};

/**
 * Formatea un RUT chileno con puntos y guión
 * @param {string} rut - RUT a formatear (sin puntos ni guión)
 * @returns {string} - RUT formateado (ej: 12.345.678-9)
 */
export const formatearRUT = (rut) => {
  // Limpiar el RUT
  const rutLimpio = rut.replace(/\./g, "").replace(/-/g, "").toUpperCase();
  
  if (rutLimpio.length <= 1) return rutLimpio;
  
  // Separar cuerpo y dígito verificador
  const cuerpo = rutLimpio.slice(0, -1);
  const dv = rutLimpio.slice(-1);
  
  // Formatear cuerpo con puntos
  let cuerpoFormateado = "";
  let contador = 0;
  
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    if (contador === 3) {
      cuerpoFormateado = "." + cuerpoFormateado;
      contador = 0;
    }
    cuerpoFormateado = cuerpo.charAt(i) + cuerpoFormateado;
    contador++;
  }
  
  return `${cuerpoFormateado}-${dv}`;
};
